import { useRef } from 'react';
import { Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { Modal } from 'components/Modals/Modal/Modal';
import { Input } from 'components/Input/InputFormik';
import Button from 'components/Button/Button';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { Level } from 'components/Modals/Modal/type';
import { slugify } from 'utils/stringUtils';
import { ICreateQuestionData } from 'types/ConfigurationQuestion.types';
import { questionFormSchema } from '../validation';
import {
  ButtonRow,
  ErrorMessage,
  FieldLabel,
  FormColumn,
  FormModalContainer,
  HelperText,
  Title,
} from './QuestionFormModal.styled';

interface IQuestionFormModalProps {
  isOpen: boolean;
  level: Level;
  // Structural shape so both server questions and local (test) questions reuse this form.
  questionForEditing: { label: string; slug: string } | null;
  onClose: () => void;
  onAdd: (data: ICreateQuestionData) => void;
  onEditLabel: (label: string) => void;
  isLoading: boolean;
}

export const QuestionFormModal = ({
  isOpen,
  level,
  questionForEditing,
  onClose,
  onAdd,
  onEditLabel,
  isLoading,
}: IQuestionFormModalProps) => {
  const { t } = useTranslation();
  const { isPhone } = useBreakpointFlag();
  const isEdit = !!questionForEditing;
  // Once the user manually edits the slug, stop auto-suggesting from the label.
  const slugManuallyEditedRef = useRef<boolean>(false);

  return (
    <Modal
      level={level}
      isOpen={isOpen}
      onClose={onClose}
      modalStyle={{
        margin: 'auto',
        width: isPhone ? '90%' : '500rem',
        minWidth: isPhone ? '90%' : 'auto',
        position: 'fixed',
      }}
    >
      <FormModalContainer>
        <Title>{isEdit ? t('Edit question') : t('Add question')}</Title>
        <Formik
          enableReinitialize
          initialValues={{
            label: questionForEditing?.label || '',
            slug: questionForEditing?.slug || '',
          }}
          validationSchema={questionFormSchema}
          validateOnChange
          validateOnMount
          onSubmit={(values) => {
            if (isEdit) {
              onEditLabel(values.label.trim());
            } else {
              onAdd({ label: values.label.trim(), slug: values.slug.trim() });
            }
          }}
        >
          {({
            errors,
            touched,
            isValid,
            setFieldValue,
            submitForm,
          }: FormikProps<{ label: string; slug: string }>) => (
            <>
              <FormColumn>
                <FieldLabel>{t('Label')}</FieldLabel>
                <Input
                  height={'41rem'}
                  name="label"
                  placeholder={t('Label')}
                  wrapperStyles={{ width: '100%' }}
                  handleOnChange={(value) => {
                    if (!isEdit && !slugManuallyEditedRef.current) {
                      setFieldValue('slug', slugify(value));
                    }
                  }}
                  errorMessage={
                    touched.label && errors.label ? t(errors.label) : ''
                  }
                />
              </FormColumn>
              <FormColumn>
                <FieldLabel>{t('Slug')}</FieldLabel>
                <Input
                  height={'41rem'}
                  name="slug"
                  placeholder={t('slug')}
                  disabled={isEdit}
                  wrapperStyles={{ width: '100%' }}
                  handleOnChange={() => {
                    slugManuallyEditedRef.current = true;
                  }}
                  errorMessage={
                    !isEdit && touched.slug && errors.slug ? t(errors.slug) : ''
                  }
                />
                <HelperText>
                  {isEdit
                    ? t('The slug cannot be changed after creation')
                    : t(
                        'Lowercase letters, numbers and hyphens only. Must start with a letter.'
                      )}
                </HelperText>
                {!isEdit && <ErrorMessage />}
              </FormColumn>
              <ButtonRow>
                <Button
                  secondary
                  label={t('Cancel')}
                  width={isPhone ? '120rem' : '200rem'}
                  onClick={onClose}
                />
                <Button
                  primary
                  disabled={!isValid || isLoading}
                  label={isEdit ? t('Save') : t('Add')}
                  width={isPhone ? '120rem' : '200rem'}
                  onClick={submitForm}
                />
              </ButtonRow>
            </>
          )}
        </Formik>
      </FormModalContainer>
    </Modal>
  );
};
